import React from 'react';
import Card from '../../components/ui/Card/Card';
import Input from '../../components/ui/Input/Input';
import Button from '../../components/ui/Button/Button';
import Modal from '../../components/ui/Modal/Modal';
import Tooltip from '../../components/ui/Tooltip/Tooltip';
import { SearchIcon } from '../../components/ui/Icons';
import { useLayout } from '../../layout/LayoutContext';
import { FEATURE_CLIENT_EMAILS } from '../../featureFlags';
import { useClientDetail } from './useClientDetail';
import {
  ALL_MODES_ON,
  ClientModeBadge,
  ClientModeSelector,
  ClientModes,
  modesOf,
} from '../Clients/clientModes';
import NotificationContactsCard from './components/NotificationContactsCard';
import ShipmentsColumn from './components/ShipmentsColumn';
import ClientDetailSkeleton from './ClientDetailSkeleton';
import styles from './ClientDetail.module.css';

const EditIcon = () => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);
const TrashIcon = () => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" />
  </svg>
);

const ListColumn: React.FC<{
  title: string;
  placeholder: string;
  items: { key: string; name: string; onClick?: () => void; onRemove?: () => void }[];
  headerAction?: React.ReactNode;
}> = ({ title, placeholder, items, headerAction }) => {
  const [q, setQ] = React.useState('');
  /**
   * ## 🔑 Paginado en el NAVEGADOR, al contrario de la columna de envíos
   *
   * Usuarios y entregas están acotados por cosas que crecen despacio: la plantilla de Dylo y el catálogo de
   * mercancías. Traerlos completos y paginar aquí no cuesta nada.
   *
   * ⚠️ Los envíos son otra cosa —crecen con la operación, sin techo— y por eso tienen su propio componente con
   * paginado **en el servidor**. Paginar los envíos aquí exigiría traerlos todos primero, que es exactamente el
   * problema que se estaba resolviendo.
   */
  const [page, setPage] = React.useState(1);
  const PER_PAGE = 15;

  const filtered = items.filter((i) => i.name.toLowerCase().includes(q.toLowerCase()));

  /** 📌 Buscar vuelve a la primera página: quedarse en la 3 mostraría vacío con un resultado que sí existe. */
  React.useEffect(() => {
    setPage(1);
  }, [q]);

  const pages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const current = Math.min(page, pages);
  const visible = filtered.slice((current - 1) * PER_PAGE, current * PER_PAGE);

  return (
    <Card className={styles.col}>
      <h3 className={styles.colTitle}>
        {title}
        {/** El total, junto al título: con la lista paginada ya no se puede obtener contando renglones. */}
        {items.length > 0 && <span className={styles.colCount}>{items.length}</span>}
      </h3>
      <div className={styles.colTools}>
        {headerAction}
        <div className={styles.colSearch}>
          <Input icon={<SearchIcon size={16} />} placeholder={placeholder} value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>
      <div className={styles.colList}>
        {filtered.length === 0 ? (
          <p className={styles.colEmpty}>{q ? 'Nothing matches that.' : 'No records.'}</p>
        ) : (
          visible.map((it) => (
            <div
              key={it.key}
              className={[styles.rowItem, it.onClick ? styles.clickable : ''].join(' ')}
              onClick={it.onClick}
            >
              <span className={styles.rowName}>{it.name}</span>
              <span className={styles.rowRight}>
                <span className={styles.miniPill}>Active</span>
                {it.onRemove && (
                  <button
                    type="button"
                    className={styles.unassignBtn}
                    onClick={(e) => {
                      e.stopPropagation();
                      it.onRemove!();
                    }}
                  >
                    UNASSIGN
                  </button>
                )}
              </span>
            </div>
          ))
        )}
      </div>

      {/**
       * 📌 El paginado solo aparece cuando hay más de una página. Aquí **sí** se pintan los números —"1 / 4"—
       * porque el total es exacto: la lista completa está en memoria. En la columna de envíos no se puede, y por eso
       * allá solo hay anterior/siguiente.
       */}
      {pages > 1 && (
        <div className={styles.colPager}>
          <button
            type="button"
            className={styles.colPagerBtn}
            disabled={current === 1}
            onClick={() => setPage(current - 1)}
          >
            Previous
          </button>
          <span className={styles.colPagerAt}>
            {current} / {pages}
          </span>
          <button
            type="button"
            className={styles.colPagerBtn}
            disabled={current === pages}
            onClick={() => setPage(current + 1)}
          >
            Next
          </button>
        </div>
      )}
    </Card>
  );
};

const ClientDetail: React.FC = () => {
  const s = useClientDetail();

  const { setHideHeader } = useLayout();
  React.useEffect(() => {
    setHideHeader(true);
    return () => setHideHeader(false);
  }, [setHideHeader]);

  const [editOpen, setEditOpen] = React.useState(false);
  const [editName, setEditName] = React.useState('');
  const [editModes, setEditModes] = React.useState<ClientModes>(ALL_MODES_ON);
  const [editErr, setEditErr] = React.useState<string | null>(null);
  const [delOpen, setDelOpen] = React.useState(false);
  const [assignOpen, setAssignOpen] = React.useState(false);
  const [assignUserId, setAssignUserId] = React.useState('');
  const [assignQuery, setAssignQuery] = React.useState('');

  React.useEffect(() => {
    if (assignOpen) {
      setAssignUserId('');
      setAssignQuery('');
    }
  }, [assignOpen]);

  if (s.loading || !s.client) return <ClientDetailSkeleton />;

  const openEdit = () => {
    setEditName(s.client.name || '');
    // Se parte del estado real del cliente, no del default: abrir y guardar no debe cambiar nada.
    setEditModes(modesOf(s.client));
    setEditErr(null);
    setEditOpen(true);
  };
  const submitEdit = async () => {
    const res = await s.editClient(editName, editModes);
    if (res === true) setEditOpen(false);
    else setEditErr(typeof res === 'string' ? res : 'Failed');
  };

  const assignedUserIds = new Set(s.userClients.map((u: any) => u.user?.id));
  const availableUsers = s.usersList.filter((u) => !assignedUserIds.has(u.id));

  return (
    <div className={`${styles.page} dylo-animate-in`}>
      <Card className={styles.headerBar}>
        <h1 className={styles.title}>{s.client.name}</h1>
        {/* El modo, visible sin abrir el modal de edición: es parte de la identidad del cliente. */}
        <ClientModeBadge client={s.client} className={styles.headerModes} />
        {/*
          Estado del cliente, junto al nombre y no dentro del modal de edición: es lo primero que hay
          que saber al abrir el detalle. `active` puede no venir si el backend es anterior al campo, y en
          ese caso no se dibuja nada — mostrar "Active" sin dato es lo que estaba mal antes.
        */}
        {s.client.active !== undefined && (
          <span
            className={[
              styles.stateBadge,
              s.client.active ? styles.stateOn : styles.stateOff,
            ].join(' ')}
          >
            {s.client.active ? 'Active' : 'Inactive'}
          </span>
        )}
        {s.perms.canEditClient && (
          <div className={styles.headerActions}>
            {/*
              🔑 El interruptor va aquí y no como casilla del modal: desactivar no es editar. Tiene
              efecto inmediato en quién puede darle trabajo al cliente, y meterlo en el formulario de
              renombrar lo volvería un cambio que se hace de paso.
            */}
            {s.client.active !== undefined && (
              <Tooltip
                content={
                  s.client.active
                    ? 'Deactivate: it stops being offered for new work'
                    : 'Reactivate the client'
                }
              >
                <button
                  className={styles.stateBtn}
                  disabled={s.busy}
                  onClick={() => s.setActive(!s.client.active)}
                >
                  {s.client.active ? 'Deactivate' : 'Activate'}
                </button>
              </Tooltip>
            )}
            <Tooltip content="Edit client">
              <button className={styles.iconBtn} onClick={openEdit}>
                <EditIcon />
              </button>
            </Tooltip>
            <Tooltip content="Delete client">
              <button className={[styles.iconBtn, styles.danger].join(' ')} onClick={() => setDelOpen(true)}>
                <TrashIcon />
              </button>
            </Tooltip>
          </div>
        )}
      </Card>

      {/**
       * Contactos del cliente para avisos automáticos por correo.
       *
       * ## 🔄 Se subió ARRIBA de la rejilla el 11-sep
       *
       * Estaba debajo, y era un error de ubicación con una consecuencia concreta que reportó el usuario: las
       * tres columnas crecen con cada carga, usuario y entrega, así que en un cliente con volumen **esta
       * sección quedaba tan abajo que se perdía de vista**. Y es la que decide a quién de fuera se le escribe.
       *
       * 🔑 El criterio: las columnas son **listas de lo que existe** y se consultan cuando se busca algo
       * concreto; esto es **configuración** y se revisa al abrir la ficha. Lo que se revisa va arriba de lo
       * que se busca.
       *
       * 📌 Sigue a todo el ancho y no como cuarta columna: su contenido —dirección, etiqueta, estado y
       * preferencias— no cabe en el ancho de "Loads" sin partirse en varios renglones.
       *
       * ⚠️ Se pasa `canEdit` en lugar de que la tarjeta consulte los permisos por su cuenta: el criterio de
       * quién puede tocar este cliente ya está resuelto arriba, y duplicarlo dejaría dos respuestas posibles
       * a la misma pregunta.
       *
       * 🔄 **Oculta para todos desde el 15-oct-2026** (`FEATURE_CLIENT_EMAILS`), hasta que el equipo confirme que
       * los avisos funcionan bien. Se vuelve a mostrar con `REACT_APP_FEATURE_CLIENT_EMAILS=true`, sin tocar código.
       */}
      {FEATURE_CLIENT_EMAILS && (
        <NotificationContactsCard clientId={s.client.id} canEdit={s.perms.canEditClient} />
      )}

      <div className={styles.grid}>
        {/**
         * 🔄 Reemplaza la columna "Loads" del 11-sep.
         *
         * La vieja traía **solo terrestre** y **todas** las cargas sin límite. Mentía por omisión —un cliente con
         * marítimo y aéreo veía una lista que se veía completa y no lo era— y no escalaba.
         *
         * 🔑 Ahora las tres modalidades en una sola columna, con búsqueda y paginado **en el servidor**. Es la
         * única de las tres columnas que lo necesita: usuarios y entregas están acotados por la plantilla y por el
         * catálogo, los envíos crecen con la operación sin techo.
         */}
        <ShipmentsColumn clientId={s.client.id} canOpen={s.perms.canViewLoads} />

        <ListColumn
          title="Users"
          placeholder="Search by name"
          headerAction={
            s.perms.canEditClient ? (
              <Button variant="primary" className={styles.addBtn} onClick={() => { setAssignUserId(''); setAssignOpen(true); }}>
                + Add User
              </Button>
            ) : undefined
          }
          items={s.userClients.map((uc: any) => ({
            key: uc.user?.id,
            name: uc.user?.name,
            onClick: s.perms.canViewUsers ? () => s.goUser(uc.user.id) : undefined,
            onRemove: s.perms.canEditClient ? () => s.unassignUser(uc.user.id) : undefined,
          }))}
        />

        <ListColumn
          title="Deliveries"
          placeholder="Search by name"
          items={s.deliveries.map((d: any) => ({
            key: d.id,
            name: d.name,
            onClick: s.perms.canViewDeliveries ? () => s.goDelivery(d.id) : undefined,
          }))}
        />
      </div>


      {/* Edit modal */}
      <Modal
        open={editOpen}
        title="Edit Client"
        onClose={() => !s.busy && setEditOpen(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditOpen(false)} disabled={s.busy}>
              Cancel
            </Button>
            <Button variant="primary" loading={s.busy} disabled={!editName.trim()} onClick={submitEdit}>
              Update
            </Button>
          </>
        }
      >
        <div className={styles.form}>
          <span className={styles.formLabel}>Name</span>
          <Input value={editName} autoFocus onChange={(e) => setEditName(e.target.value)} />

          <span className={styles.formLabel}>Modes</span>
          <ClientModeSelector value={editModes} onChange={setEditModes} disabled={s.busy} />

          {editErr && <span className={styles.formErr}>{editErr}</span>}
        </div>
      </Modal>

      {/* Delete confirm */}
      <Modal
        open={delOpen}
        title="Delete Client"
        onClose={() => !s.busy && setDelOpen(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setDelOpen(false)} disabled={s.busy}>
              Cancel
            </Button>
            <Button variant="primary" className={styles.dangerBtn} loading={s.busy} onClick={() => s.remove()}>
              Delete
            </Button>
          </>
        }
      >
        <p className={styles.confirmText}>
          Are you sure you want to delete <b>{s.client.name}</b>? This action cannot be undone.
        </p>
      </Modal>

      {/* Assign user modal */}
      <Modal
        open={assignOpen}
        title="Assign User"
        onClose={() => !s.busy && setAssignOpen(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setAssignOpen(false)} disabled={s.busy}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={s.busy}
              disabled={!assignUserId}
              onClick={async () => {
                await s.assignUser(assignUserId);
                setAssignOpen(false);
              }}
            >
              Assign
            </Button>
          </>
        }
      >
        <div className={styles.assignBox}>
          <Input
            icon={<SearchIcon size={16} />}
            placeholder="Search a user"
            value={assignQuery}
            onChange={(e) => setAssignQuery(e.target.value)}
          />
          <div className={styles.assignList}>
            {availableUsers.filter((u) => u.name.toLowerCase().includes(assignQuery.toLowerCase())).length === 0 ? (
              <p className={styles.colEmpty}>No users available.</p>
            ) : (
              availableUsers
                .filter((u) => u.name.toLowerCase().includes(assignQuery.toLowerCase()))
                .map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    className={[styles.assignRow, assignUserId === u.id ? styles.assignRowActive : ''].join(' ')}
                    onClick={() => setAssignUserId(u.id)}
                  >
                    <span className={[styles.assignRadio, assignUserId === u.id ? styles.assignRadioOn : ''].join(' ')} />
                    <span className={styles.rowName}>{u.name}</span>
                  </button>
                ))
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ClientDetail;
